/**
 * State for the Common Balance Pool builder, following the package's builder-state convention
 * (cf. TPAllocationState / CalculationBuilderState): entity + isLoading/error, a `needsRefresh` flag
 * that drives the fetch effect, and the cross-component modal / lifecycle state.
 */
import { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import { CommonBalancePool, CommonBalancePoolColumn } from 'src/models/multidimensional-allocation/CommonBalancePool';

/**
 * One Step-1 input row: a source object and the columns it contributes (matching the CBP mockup).
 * Owned here in the service layer so `editingRow` doesn't create a service→component dependency;
 * the component-side helpers in `cbpInputs.ts` import and re-export this type.
 */
export interface InputRow {
    num: number;
    sourceType?: string;
    sourceObject: string;
    sourceKey: string;
    /** Display names, for the Columns badges. */
    columns: string[];
    /** The underlying INPUT columns backing this row, so Edit can rebuild them. */
    cols: CommonBalancePoolColumn[];
}

export interface CommonBalancePoolBuilderState {
    pool?: CommonBalancePool;
    isLoading: boolean;
    error?: string;
    /** Set true to (re)fetch the pool; the reducer clears it on SET_POOL. */
    poolNeedsRefresh: boolean;

    /** True while a column persist (UpdateCommonBalancePool) is in flight. */
    saving: boolean;
    saveError?: string;

    /** True while Step 2 (calculated columns) has unsaved edits — guards the lifecycle/input actions. */
    step2Dirty: boolean;

    /** Add/Edit-input modal. `editingRow` present => edit mode for that input. */
    addModalVisible: boolean;
    editingRow?: InputRow;

    /** Selected accounting period (for Execute). */
    period?: SelectProps.Option | null;

    /** Which top-level lifecycle action is in flight. */
    actionBusy: 'save' | 'submit' | 'execute' | '';
    actionError?: string;
    actionNotice?: string;
}

export const initialState: CommonBalancePoolBuilderState = {
    isLoading: true,
    poolNeedsRefresh: true,
    saving: false,
    step2Dirty: false,
    addModalVisible: false,
    actionBusy: '',
};

export const getInitialState = (): CommonBalancePoolBuilderState => ({ ...initialState });
